import { useState, useEffect } from "react";
import { useTitanApp } from "@/hooks/useTitanApp";
import {
  getPatientPrescriptions,
  updatePatientPrescription,
} from "../../dispatchers/patientPrescriptionDispatcher";
import { json } from "zod";
import {
  getDefaultCompanyServices,
  setShowNHS111ReferralTab,
} from "@/dispatchers/pharmacyServiceDispatchers";
import { getUserCertificate } from "@/dispatchers/onboardingVerificationDsipatchers";
import {
  getApplicationData,
  setCurrentGlobalTabValue,
  setCurrentGlobalTabText,
  setisPatientSearched,
} from "@/dispatchers/appDispatchers";
import {
  addPatientConsultation,
  setConsultationId,
  setFreeTypeMode,
  setIsFinalSummary,
  setPatientNotes,
  setSignatureDetails,
  setTranscript,
  setConsultationPaused,
  setCurrentConsultationMediaButtonMode,
  setIsConsultationEnabled,
  setPatientTranscription,
  setSelectedPatientConsultation,
  setShowPlayPauseBtn,
  setAiChatSuccessMessages,
  setAddPatientAIChatMessage,
  setPreConsultationTemplate,
  setPreConsultationTranscription,
  setisHandleStartConsultation,
  getPatientConsultationByConsultationId,
  getPatientByPatientId,
  getPreConsultationByConsultationId,
  getPaymentAuditByConsultation,
  setPatientTabSwitcher,
  setHasPreConsultTab,
  setSnomedCodes,
  updateConsultationTemplate,
  getPatientConsultations,
} from "@/dispatchers/patientDispatchers";
import {
  setSelectedTemplate,
  setIsTemplateSelected,
  setStreamedText,
  sendAIQAService,
} from "@/dispatchers/templateDispatchers";
import { ConsultationButtonStatus } from "@/enum/ConsultationButtonStatus";
import { useNavigate } from "react-router-dom";
import { setPatientInfoTab } from "@/dispatchers/appDispatchers";
import { useNHS111Data } from "@/lib/nhs111/nhs111Data";
import { getCompanyDocumentsByCompanyId } from "@/dispatchers/companyDocumentsDispatchers";
import { clearLinkedTreatmentPlan } from "@/dispatchers/treatmentPlanDispatchers";
import { extractSnomedCodesFromSummary } from "@/utils/extractSnomedCodesFromSummary";
import { isNhsServiceByServiceId } from "@/components/nhs-templates/NHSServicesHelper";

interface Service {
  _id: string;
  serviceName: string;
}

export const usePrescriptionApprovalData = () => {
  const handleUpdatePrescription = async (data: any) => {
    await updatePatientPrescription(data);
  };

  const { appState, patientPrescriptionState, pharmacyServiceState } =
    useTitanApp();
  const { companyId, currentUser, currentPharmacyId, features, darkMode } =
    appState;
  const { ifPharmacyDashboardEnabled } = features || {};

  const {
    isPatientPrescriptionsLoading,
    patientPrescriptions,
    showPrintPreviewLoading,
  } = patientPrescriptionState || {};

  const { isPharmacyServiceLoading, defaultPharmacyServices } =
    pharmacyServiceState || {};

  const [prescriptions, setPrescriptions] = useState([]);
  const [services, setServices] = useState([]);
  const navigate = useNavigate();
  const { setNHS111ConsultationSummary } = useNHS111Data();

  const { pharmacies } = appState;

  const getInitials = (
    foreName?: string,
    middleName?: string,
    surName?: string,
  ) => {
    return [foreName, middleName, surName]
      .filter(Boolean)
      .map((name) => name.charAt(0).toUpperCase())
      .join("");
  };
  const statusMap: Record<number, string> = {
    1: "pending",
    2: "approved",
    3: "rejected",
  };

  const handleNavigateToConsultation = async (patientId, consultation) => {
    var consultationData = await getPatientConsultationByConsultationId(
      consultation.consultationId,
    );
    setNHS111ConsultationSummary(consultationData);
    setHasPreConsultTab(false);
    const patient = await getPatientByPatientId(patientId);
    setFreeTypeMode(false);
    setIsConsultationEnabled(false);
    setSelectedPatientConsultation(consultationData);
    setConsultationId(consultationData?.consultationId);
    setPatientTranscription(consultationData?.transcription);
    setTranscript(consultationData?.transcription);
    setSelectedTemplate(consultationData?.template);
    setSignatureDetails(consultationData?.patientConsent);
    setIsFinalSummary(consultationData?.notes !== null);
    setPatientNotes(consultationData?.notes ?? "");
    setConsultationPaused(
      consultationData?.consultationStatus === 3 &&
        !!consultationData?.transcription?.trim()
        ? true
        : false,
    );
    setShowPlayPauseBtn(consultationData?.consultationStatus === 3);

    const { mainSummary, snomedCodes } = extractSnomedCodesFromSummary(
      consultationData?.consultationSummary,
    );
    setStreamedText(mainSummary);
    setSnomedCodes(snomedCodes);
    await getPatientConsultations(patientId);

    await getPaymentAuditByConsultation({
      consultationID: consultationData?.consultationId,
    });
    setisHandleStartConsultation(false);
    if (
      consultationData?.template != null &&
      (consultationData?.consultationStatus === 2 ||
        consultationData?.consultationStatus === 3)
    ) {
      setIsTemplateSelected(true);
    }
    if (
      consultationData?.consultationButtonStatus ===
      ConsultationButtonStatus.Manual
    ) {
      setCurrentConsultationMediaButtonMode("manual");
    }

    if (consultationData?.template?.linkedDocumentId && companyId) {
      getCompanyDocumentsByCompanyId(companyId);
    }

    /* All Pre Consult Activities */
    if (consultationData?.preConsultationTemplate) {
      const preConsultDataRes = await getPreConsultationByConsultationId({
        consultationId: consultationData?.consultationId,
      });
      setPreConsultationTemplate(preConsultDataRes?.preConsultationTemplate);
      if (preConsultDataRes?.preConsultationTemplate) {
        let preConsultTranscription = "";
        preConsultDataRes?.preConsultationTemplate?.templateSections?.forEach(
          (section) => {
            section?.userControls?.forEach((control) => {
              if (control.label && control.value) {
                preConsultTranscription += ` ${control.label} ${control.value} `;
              }
            });
          },
        );
        setPreConsultationTranscription(preConsultTranscription.trim());
      } else {
        setPreConsultationTranscription("");
      }
    } else {
      setPreConsultationTemplate(null);
      setPreConsultationTranscription("");
    }

    clearLinkedTreatmentPlan();
    setCurrentGlobalTabValue(ifPharmacyDashboardEnabled ? 4 : 3);
    setCurrentGlobalTabText("Patients");
    setisPatientSearched(true);
    setShowNHS111ReferralTab(false);
    navigate(
      `/pharmacy/patientconsultation/${currentPharmacyId}/${patientId}/${consultation?.consultationId}`,
    );

    setPatientInfoTab(1);
    setPatientTabSwitcher(consultationData?.preConsultationTemplate ? 1 : 0);

    if (consultationData?.consultationChat) {
      try {
        const parsedAiChatMessages = JSON.parse(
          consultationData.consultationChat,
        );
        setAiChatSuccessMessages();
        parsedAiChatMessages.forEach((message) => {
          setAddPatientAIChatMessage(message);
        });
      } catch (err) {
        console.error("Invalid consultationChat JSON:", err);
      }
    }

    const filledCount = countFilledControls(consultationData?.template);
    if (filledCount < 1 && consultationData?.transcription?.trim()) {
      const allUserControls =
        consultationData?.template?.templateSections?.flatMap(
          (sec) => sec.userControls,
        ) || [];
      const questions = allUserControls.map((ctrl, idx) => ({
        [`q${idx + 1}`]: ctrl.label,
      }));
      const qaResponse = await sendAIQAService({
        q: questions,
        t: consultationData?.transcription,
      });
      const updatedTemplate = fillTemplateWithQA(
        consultationData?.template,
        qaResponse,
      );
      setSelectedTemplate(updatedTemplate);
      if (updatedTemplate?.templateSections?.length) {
        await updateConsultationTemplate({
          consultationId: consultationData?.consultationId,
          payload: updatedTemplate.templateSections,
        });
      }
    } else {
      setSelectedTemplate(consultationData?.template);
    }
  };

  function countFilledControls(template) {
    let count = 0;
    template?.templateSections?.forEach((section) => {
      section.userControls.forEach((ctrl) => {
        if (ctrl.value && ctrl.value?.trim() !== "") count++;
      });
    });
    return count;
  }

  function fillTemplateWithQA(template, qaResponse) {
    let answers = {};
    try {
      answers = JSON.parse(qaResponse?.response || "{}");
    } catch (e) {
      console.error("Invalid QA response JSON", e);
    }
    let counter = 1;
    const updatedSections = template?.templateSections?.map((section) => ({
      ...section,
      userControls: section?.userControls?.map((ctrl) => {
        const key = `a${counter}`;
        const newValue =
          answers.hasOwnProperty(key) && answers[key] !== null
            ? answers[key]
            : ctrl.value;
        counter++;
        return { ...ctrl, value: newValue };
      }),
    }));
    return {
      ...template,
      templateSections: updatedSections,
    };
  }

  useEffect(() => {
    if (patientPrescriptions && patientPrescriptions.length > 0) {
      const mappedPrescriptions = patientPrescriptions.map((prescription) => ({
        id: prescription?.patientPrescription?._id,
        patientName: prescription?.patient?.patientName || "",
        patientInitials:
          getInitials(
            prescription?.patient?.foreName,
            prescription?.patient?.middleName,
            prescription?.patient?.surName,
          ) || "",
        patientNHS: prescription?.patient?.nhsNumber || "",
        medications: (prescription?.patientPrescription?.drugs || []).map(
          (drug) => ({
            id: Math.random().toString(36).substr(2, 9),
            medication: drug.drugName || "",
            dosage: drug.dose || "",
            quantity: drug.quantity || "",
            duration: drug.duration || "",
            indication: drug.indication || "",
            notes: drug.note || "",
            prescriptionItemId: drug.prescriptionItemId,
          }),
        ),
        prescribedBy: prescription?.patientPrescription?.createdByName || "",
        consultationDate: prescription?.patientConsultation?.createdOn
          ? new Date(prescription.patientConsultation.createdOn)
              .toISOString()
              .split("T")[0]
          : "",

        consultationTime: prescription?.patientConsultation?.createdOn
          ? new Date(prescription.patientConsultation.createdOn)
              .toISOString()
              .split("T")[1]
              .slice(0, 5) // HH:mm
          : "",

        priority: prescription?.priority || "",
        serviceType: prescription?.patientPrescription?.serviceName || "",
        allergies: (prescription?.patient?.allergies || []).map(
          (allergy) => allergy.allergyName,
        ),
        notes: prescription?.patientConsultation?.notes || "",
        status:
          statusMap[prescription?.patientPrescription?.status] || "Pending",
        prescriptionImageUrl:
          prescription?.patientPrescription?.base64Image || "",
        summary: isNhsServiceByServiceId(
          prescription?.patientPrescription?.serviceID,
        )
          ? prescription?.patientConsultation?.shortSummary
          : prescription?.patientConsultation?.consultationSummary || "",
        pharmacyID: prescription?.patientPrescription?.pharmacyID,
        prescriptionCategory:
          prescription?.patientPrescription?.prescriptionCategory,
      }));

      setPrescriptions(mappedPrescriptions);
    } else {
      // reset to empty list if no data
      setPrescriptions([]);
    }
  }, [patientPrescriptions]);

  useEffect(() => {
    if (defaultPharmacyServices && defaultPharmacyServices.length > 0) {
      const mappedServices: Service[] = defaultPharmacyServices.map(
        (service) => ({
          _id: service._id,
          serviceName: service.serviceName,
          serviceType: service.serviceType,
        }),
      );
      setServices(mappedServices);
    }
  }, [defaultPharmacyServices]);
  return {
    prescriptions,
    darkMode,
    setPrescriptions,
    isPatientPrescriptionsLoading,
    isPharmacyServiceLoading,
    services,
    handleNavigateToConsultation,
    showPrintPreviewLoading,
  };
};
